import type {
  DataType,
  FieldDefinition,
  FieldLevel,
  FilterCondition,
  FilterOperator,
  FilterSpec,
  Meta,
} from '@numistic/contracts'
import { fieldsForType, operatorsFor, type ResolvedField } from '@numistic/domain'
import { useTranslation } from '@numistic/i18n'
import { Button, Input, Select } from '@numistic/ui'
import { useId, useMemo, useState } from 'react'

import { widgets } from './widgets/index.js'
import { useRefOptions } from './widgets/ref-options.js'

/** Поля, по которым можно фильтровать: для одного типа или объединение по всем типам. */
export function filterableFields(
  meta: Meta,
  level: FieldLevel,
  typeCode?: string,
): FieldDefinition[] {
  const types = typeCode ? [typeCode] : meta.itemTypes.map((x) => x.code)
  const seen = new Map<string, FieldDefinition>()
  for (const code of types)
    for (const f of fieldsForType(meta, code, level))
      if (f.def.ui.filterable) seen.set(f.def.code, f.def)
  return [...seen.values()]
}

const NUMERIC = new Set<DataType>(['integer', 'decimal', 'year_range'])

/** Значение условия в типах FilterSpec (числа — числами). */
function normalize(def: FieldDefinition, op: FilterOperator, value: unknown): unknown {
  const num = (v: unknown) =>
    v === undefined || v === '' ? undefined : Number(String(v).replace(',', '.'))
  if (op === 'is_set' || op === 'is_not_set') return undefined
  if (op === 'between') {
    const [a, b] = (value as [unknown, unknown] | undefined) ?? []
    return NUMERIC.has(def.dataType) ? [num(a), num(b)] : [a, b]
  }
  if (NUMERIC.has(def.dataType) && (op === 'eq' || op === 'gte' || op === 'lte')) return num(value)
  return value
}

function asField(def: FieldDefinition, dataType: DataType = def.dataType): ResolvedField {
  return {
    def: { ...def, dataType },
    required: false,
    section: def.ui.section,
    sortKey: '',
    override: false,
  }
}

export function FilterBuilder({
  meta,
  value,
  onChange,
  level = 'catalog',
  typeCode,
}: {
  meta: Meta
  value: FilterSpec
  onChange: (spec: FilterSpec) => void
  level?: FieldLevel
  typeCode?: string
}) {
  const { t } = useTranslation()
  const fields = useMemo(() => filterableFields(meta, level, typeCode), [meta, level, typeCode])
  const byCode = useMemo(() => new Map(fields.map((f) => [f.code, f])), [fields])

  // Стабильные ключи строк: при удалении строки из середины черновики значений не «переезжают».
  const [rows, setRows] = useState<{ ids: number[]; next: number }>(() => ({
    ids: value.and.map((_, i) => i),
    next: value.and.length,
  }))
  if (rows.ids.length !== value.and.length) {
    const ids = rows.ids.slice(0, value.and.length)
    let next = rows.next
    while (ids.length < value.and.length) ids.push(next++)
    setRows({ ids, next })
  }
  const removeRow = (index: number) => {
    setRows((r) => ({ ...r, ids: r.ids.filter((_, i) => i !== index) }))
    onChange({ and: value.and.filter((_, i) => i !== index) })
  }

  const update = (index: number, patch: Partial<FilterCondition>) =>
    onChange({ and: value.and.map((c, i) => (i === index ? { ...c, ...patch } : c)) })

  const add = () => {
    const def = fields[0]
    if (def)
      onChange({ and: [...value.and, { field: def.code, op: operatorsFor(def.dataType)[0]! }] })
  }

  return (
    <div className="grid gap-3" role="group" aria-label={t('filter.title')}>
      {value.and.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('filter.empty')}</p>
      ) : null}
      {value.and.map((condition, index) => {
        const def = byCode.get(condition.field)
        if (!def) return null
        const ops = operatorsFor(def.dataType)
        return (
          <div
            key={rows.ids[index] ?? `new-${index}`}
            className="grid items-start gap-2 md:grid-cols-[14rem_10rem_1fr_auto]"
            data-testid="filter-row"
          >
            <Select
              aria-label={t('filter.field')}
              value={condition.field}
              onChange={(e) => {
                const next = byCode.get(e.target.value)!
                update(index, {
                  field: next.code,
                  op: operatorsFor(next.dataType)[0]!,
                  value: undefined,
                })
              }}
            >
              {fields.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.name.ru}
                </option>
              ))}
            </Select>
            <Select
              aria-label={t('filter.operator')}
              value={condition.op}
              onChange={(e) =>
                update(index, { op: e.target.value as FilterOperator, value: undefined })
              }
            >
              {ops.map((op) => (
                <option key={op} value={op}>
                  {t(`filter.ops.${op}`)}
                </option>
              ))}
            </Select>
            <ValueEditor
              key={`${rows.ids[index] ?? index}:${condition.field}:${condition.op}`}
              meta={meta}
              def={def}
              condition={condition}
              typeCode={typeCode}
              onChange={(v) => update(index, { value: normalize(def, condition.op, v) })}
            />
            <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(index)}>
              {t('common.remove')}
            </Button>
          </div>
        )
      })}
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={!fields.length}>
          {t('filter.add')}
        </Button>
        {value.and.length ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ and: [] })}>
            {t('filter.clear')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function ValueEditor({
  meta,
  def,
  condition,
  typeCode,
  onChange,
}: {
  meta: Meta
  def: FieldDefinition
  condition: FilterCondition
  typeCode?: string
  onChange: (value: unknown) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  const { op } = condition
  const label = t('filter.value')
  // Ввод хранится как набран («8,» при наборе «8,5»), наружу уходит нормализованное значение.
  const [raw, setRaw] = useState<unknown>(condition.value)
  const change = (v: unknown) => {
    setRaw(v)
    onChange(v)
  }
  if (op === 'is_set' || op === 'is_not_set') return <span />

  if (op === 'between') {
    const [a, b] = (raw as [unknown, unknown] | undefined) ?? []
    const type = def.dataType === 'date' ? 'date' : 'text'
    return (
      <div className="flex items-center gap-2">
        <Input
          aria-label={`${label} (${t('meta.yearFrom')})`}
          type={type}
          inputMode="decimal"
          value={a === undefined ? '' : String(a)}
          onChange={(e) => change([e.target.value, b])}
        />
        <span className="text-muted-foreground">—</span>
        <Input
          aria-label={`${label} (${t('meta.yearTo')})`}
          type={type}
          inputMode="decimal"
          value={b === undefined ? '' : String(b)}
          onChange={(e) => change([a, e.target.value])}
        />
      </div>
    )
  }

  if (op === 'in') {
    if (def.dataType === 'ref')
      return (
        <RefMulti meta={meta} def={def} value={raw as string[] | undefined} onChange={change} />
      )
    const Multi = widgets.multi_enum
    return (
      <Multi id={id} meta={meta} field={asField(def, 'multi_enum')} value={raw} onChange={change} />
    )
  }

  // Для «содержит» по спискам и точного года по диапазону — простой ввод.
  const dataType: DataType =
    def.dataType === 'year_range'
      ? 'integer'
      : def.dataType === 'list' || op === 'contains'
        ? 'text'
        : def.dataType
  const Widget = widgets[dataType]
  return (
    <div aria-label={label}>
      <Widget
        id={id}
        meta={meta}
        field={asField(def, dataType)}
        typeCode={typeCode}
        value={raw}
        onChange={change}
      />
    </div>
  )
}

function RefMulti({
  meta,
  def,
  value,
  onChange,
}: {
  meta: Meta
  def: FieldDefinition
  value?: string[]
  onChange: (v: unknown) => void
}) {
  const { t } = useTranslation()
  const { options } = useRefOptions(def.refKind, meta)
  return (
    <select
      multiple
      aria-label={t('filter.value')}
      className="h-24 w-full rounded-md border border-input bg-card px-2 py-1 text-sm"
      value={value ?? []}
      onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  )
}
